<template>
  <Teleport :to="store.overlayTarget">
    <div
      id="event-popover-backdrop"
      v-if="store.eventPopoverOpen"
      class="fixed inset-0 z-40"
      @click="store.closeEventPopover()"
    ></div>

    <Transition name="popover">
      <div
        v-if="store.eventPopoverOpen && booking"
        id="event-popover"
        ref="popoverEl"
        class="fixed z-50 w-[440px] flex flex-col bg-white dark:bg-[#1C2128] rounded-xl shadow-2xl border border-gray-100 dark:border-gray-700 overflow-hidden"
        :style="popoverStyle"
        @click.stop
      >
        <!-- Header with tinted background — fixed, never scrolls -->
        <div id="event-popover-header" class="flex-shrink-0 px-4 pt-3 pb-4" :style="{ backgroundColor: eventColor + '18' }">
          <!-- Close + actions row -->
          <div class="flex items-center justify-between mb-3">
            <div class="flex items-center gap-1">
              <!-- Edit -->
              <button
                id="event-popover-edit-btn"
                @click="openEdit"
                class="w-7 h-7 flex items-center justify-center rounded-full hover:bg-black/10 transition-colors"
                :title="booking.html_link ? 'Edit in Google Calendar' : 'Edit'"
              >
                <svg class="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                </svg>
              </button>
              <!-- Private notes -->
              <button
                id="event-popover-notes-btn"
                @click="notesOpen = true"
                class="relative w-7 h-7 flex items-center justify-center rounded-full hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
                title="Private notes"
              >
                <svg class="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                <span
                  v-if="booking.internal_notes"
                  id="event-popover-notes-indicator"
                  class="absolute top-0.5 right-0.5 w-2 h-2 bg-blue-500 rounded-full ring-2 ring-white dark:ring-[#1C2128]"
                ></span>
              </button>
              <!-- Delete -->
              <button
                id="event-popover-delete-btn"
                @click="confirmDelete"
                :disabled="deleting"
                class="w-7 h-7 flex items-center justify-center rounded-full hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors disabled:opacity-40"
                title="Delete"
              >
                <svg v-if="!deleting" class="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                </svg>
                <svg v-else class="w-3.5 h-3.5 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                </svg>
              </button>
            </div>
            <!-- Close -->
            <button
              id="event-popover-close-btn"
              @click="store.closeEventPopover()"
              class="w-7 h-7 flex items-center justify-center rounded-full hover:bg-black/10 transition-colors"
            >
              <svg class="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <!-- Color dot + title + date -->
          <div class="flex items-start gap-3">
            <div
              id="event-popover-color-dot"
              class="w-4 h-4 rounded mt-1.5 flex-shrink-0"
              :style="{ backgroundColor: eventColor }"
            ></div>
            <div>
              <h3 id="event-popover-title" class="text-xl font-semibold text-gray-900 dark:text-white leading-snug">
                {{ booking.title || booking.name }}
              </h3>
              <p id="event-popover-date" class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{{ dateText }}</p>
            </div>
          </div>
        </div>

        <!-- Body — the only scrollable region -->
        <div id="event-popover-body" class="flex-1 overflow-y-auto overflow-x-hidden px-4 pt-3 pb-6 space-y-3">
          <!-- Time (timed events) -->
          <div v-if="!booking.all_day && timeText" id="event-popover-time" class="flex items-center gap-3">
            <svg class="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <p class="text-sm text-gray-700 dark:text-gray-200">
              {{ timeText }}
              <span class="text-xs text-gray-400 dark:text-gray-500">· {{ durationText }}</span>
            </p>
          </div>

          <!-- Google Meet -->
          <div v-if="booking.google_meet_link" id="event-popover-meet-link" class="flex items-start gap-3">
            <svg class="w-4 h-4 flex-shrink-0 mt-2" viewBox="0 0 87.5 72" aria-hidden="true">
              <path fill="#00832d" d="M49.5 36l8.53 9.75 11.47 7.33 2-17.02-2-16.64-11.69 6.44z"/>
              <path fill="#0066da" d="M0 51.5V66c0 3.315 2.685 6 6 6h14.5l3-10.96-3-9.54-9.95-3z"/>
              <path fill="#e94235" d="M20.5 0L0 20.5l10.55 3 9.95-3 2.95-9.41z"/>
              <path fill="#2684fc" d="M20.5 20.5H0v31h20.5z"/>
              <path fill="#00ac47" d="M82.6 8.68L69.5 19.42v33.66l13.16 10.79c1.97 1.54 4.85.135 4.85-2.37V11c0-2.535-2.945-3.925-4.91-2.32zM49.5 36v15.5h-29V72h43c3.315 0 6-2.685 6-6V53.08z"/>
              <path fill="#ffba00" d="M63.5 0h-43v20.5h29V36l20-16.57V6c0-3.315-2.685-6-6-6z"/>
            </svg>
            <div class="flex-1 min-w-0">
              <a
                :href="booking.google_meet_link"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Join with Google Meet
              </a>
              <div class="flex items-center justify-between gap-1 mt-1.5 min-w-0">
                <p class="text-xs text-gray-400 dark:text-gray-500 truncate">{{ meetLinkDisplay }}</p>
                <button
                  id="event-popover-meet-link-copy-btn"
                  @click="copyMeetLink"
                  class="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  :title="meetLinkCopied ? 'Copied' : 'Copy conference link'"
                >
                  <svg v-if="meetLinkCopied" class="w-3.5 h-3.5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                  </svg>
                  <svg v-else class="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <!-- Location: main part darker, remainder lighter below -->
          <div v-if="locationParts" id="event-popover-location" class="flex items-start gap-3">
            <svg class="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
            </svg>
            <div class="flex-1 min-w-0">
              <div class="flex items-center justify-between gap-1">
                <p class="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{{ locationParts.main }}</p>
                <button
                  id="event-popover-location-copy-btn"
                  @click="copyLocation"
                  class="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  :title="locationCopied ? 'Copied' : 'Copy location'"
                >
                  <svg v-if="locationCopied" class="w-3.5 h-3.5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                  </svg>
                  <svg v-else class="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                  </svg>
                </button>
              </div>
              <p v-if="locationParts.secondary" class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{{ locationParts.secondary }}</p>
            </div>
          </div>

          <!-- Visibility -->
          <div v-if="visibilityText" id="event-popover-visibility" class="flex items-center gap-3">
            <svg class="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
            </svg>
            <span class="text-sm text-gray-700 dark:text-gray-200">{{ visibilityText }}</span>
          </div>

          <!-- Transparency (Busy / Free) -->
          <div v-if="transparencyText" id="event-popover-transparency" class="flex items-center gap-3">
            <svg class="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
            </svg>
            <span class="text-sm text-gray-700 dark:text-gray-200">{{ transparencyText }}</span>
          </div>

          <!-- Guests -->
          <div v-if="guests.length" id="event-popover-guests" class="flex items-start gap-3">
            <svg class="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
            </svg>
            <div class="flex-1 min-w-0">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm text-gray-700 dark:text-gray-200">
                    {{ guests.length }} {{ guests.length === 1 ? 'guest' : 'guests' }}
                  </p>
                  <p v-if="rsvpSummaryText" class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{{ rsvpSummaryText }}</p>
                </div>
                <div class="flex items-center flex-shrink-0">
                  <button
                    v-if="isGuestsCollapsible"
                    id="event-popover-guests-toggle-btn"
                    @click="guestsExpanded = !guestsExpanded"
                    class="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    :aria-expanded="guestsExpanded"
                    :title="guestsExpanded ? 'Hide guests' : 'Show guests'"
                  >
                    <svg
                      class="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 transition-transform"
                      :class="guestsExpanded ? 'rotate-180' : ''"
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                    </svg>
                  </button>
                  <button
                    id="event-popover-guests-copy-btn"
                    @click="copyGuestEmails"
                    class="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    :title="guestsCopied ? 'Copied' : 'Copy guest emails'"
                  >
                    <svg v-if="guestsCopied" class="w-3.5 h-3.5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                    </svg>
                    <svg v-else class="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                    </svg>
                  </button>
                </div>
              </div>
              <div v-if="!isGuestsCollapsible || guestsExpanded" id="event-popover-guests-list" class="mt-2 space-y-2">
                <div
                  v-for="guest in guests"
                  :key="guest.email"
                  :id="`event-popover-guest-${guest.email}`"
                  class="flex items-center gap-2.5 min-w-0"
                  @mouseenter="onGuestEnter(guest, $event)"
                  @mouseleave="onGuestLeave"
                >
                  <div class="relative w-8 h-8 flex-shrink-0">
                    <img
                      v-if="guestPicture(guest)"
                      :src="guestPicture(guest)"
                      :alt="guest.email"
                      class="w-8 h-8 rounded-full object-cover"
                    />
                    <div
                      v-else
                      class="w-8 h-8 rounded-full flex items-center justify-center"
                      :class="hasResponded(guest) ? 'bg-blue-100 dark:bg-blue-900/40' : 'bg-gray-100 dark:bg-gray-700'"
                    >
                      <span
                        class="text-[8px] font-semibold uppercase"
                        :class="hasResponded(guest) ? 'text-blue-600 dark:text-blue-300' : 'text-gray-500 dark:text-gray-400'"
                      >{{ (guest.display_name || guest.email || '?')[0] }}</span>
                    </div>
                    <!-- RSVP badge: accepted / declined / tentative -->
                    <div
                      v-if="guest.response_status === 'accepted'"
                      class="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full flex items-center justify-center ring-2 ring-white dark:ring-[#1C2128]"
                      title="Accepted"
                    >
                      <svg class="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" stroke-width="3.5" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/>
                      </svg>
                    </div>
                    <div
                      v-else-if="guest.response_status === 'declined'"
                      class="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-red-500 rounded-full flex items-center justify-center ring-2 ring-white dark:ring-[#1C2128]"
                      title="Declined"
                    >
                      <svg class="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" stroke-width="3.5" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                      </svg>
                    </div>
                    <div
                      v-else-if="guest.response_status === 'tentative'"
                      class="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-gray-400 dark:bg-gray-500 rounded-full flex items-center justify-center ring-2 ring-white dark:ring-[#1C2128]"
                      title="Maybe"
                    >
                      <span class="text-[9px] font-bold text-white leading-none">?</span>
                    </div>
                  </div>
                  <div class="min-w-0">
                    <div class="flex items-center gap-1 min-w-0">
                      <p
                        class="text-sm text-gray-700 dark:text-gray-200 truncate"
                        :class="guest.timelydo_user ? 'underline decoration-dotted decoration-gray-400 underline-offset-2 cursor-pointer' : ''"
                      >{{ guest.display_name || guest.email }}</p>
                    </div>
                    <p v-if="guest.organizer" class="text-xs text-gray-400 dark:text-gray-500">Organizer</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Description -->
          <div v-if="safeBody" id="event-popover-description" class="flex items-start gap-3">
            <svg class="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h10"/>
            </svg>
            <div
              class="text-sm text-gray-700 dark:text-gray-200 break-words min-w-0
                     [&_a]:text-blue-600 [&_a]:underline dark:[&_a]:text-blue-400
                     [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5
                     [&_p]:mb-1 [&_img]:max-w-full [&_img]:rounded"
              v-html="safeBody"
            ></div>
          </div>

          <!-- Calendar name -->
          <div v-if="booking.calendar_name" id="event-popover-calendar-name" class="flex items-center gap-3 pt-3">
            <svg class="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
            </svg>
            <span class="text-sm text-gray-700 dark:text-gray-200 truncate">{{ booking.calendar_name }}</span>
          </div>

          <!-- Delete confirmation -->
          <div v-if="showDeleteConfirm" id="event-popover-delete-confirm" class="pt-1 border-t border-gray-100 dark:border-gray-700">
            <p class="text-sm text-gray-600 dark:text-gray-300 mb-2">Delete this booking?</p>
            <div class="flex gap-2">
              <button
                id="event-popover-delete-confirm-yes-btn"
                @click="deleteBooking"
                :disabled="deleting"
                class="px-3 py-1.5 text-xs font-medium bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg transition-colors"
              >
                {{ deleting ? 'Deleting...' : 'Yes, delete' }}
              </button>
              <button
                id="event-popover-delete-confirm-cancel-btn"
                @click="showDeleteConfirm = false"
                class="px-3 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>

          <p v-if="error" id="event-popover-error" class="text-xs text-red-500 dark:text-red-400">{{ error }}</p>
        </div>

        <!-- Going? RSVP footer — fixed, never scrolls -->
        <div
          v-if="showRsvp"
          id="event-popover-rsvp-footer"
          class="flex-shrink-0 flex items-center justify-between px-4 py-3 border-t border-gray-100 dark:border-gray-700"
        >
          <span class="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
            Going?
            <svg v-if="rsvpSaving !== null" id="event-popover-rsvp-saving-spinner" class="w-3.5 h-3.5 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
            </svg>
          </span>
          <div class="flex gap-1.5">
            <button
              v-for="opt in RSVP_OPTIONS"
              :key="opt.value"
              :id="`event-popover-rsvp-${opt.value}-btn`"
              @click="setRsvp(opt.value)"
              :disabled="rsvpSaving !== null"
              class="px-3 py-1 text-xs font-medium rounded-full border transition-colors disabled:opacity-60"
              :class="currentRsvp === opt.value
                ? 'bg-blue-600 border-blue-600 text-white'
                : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'"
            >
              {{ opt.label }}
            </button>
          </div>
        </div>
        <div
          v-else-if="rsvpLocked"
          id="event-popover-rsvp-locked-footer"
          class="flex-shrink-0 px-4 py-3 border-t border-gray-100 dark:border-gray-700"
        >
          <p class="text-xs text-gray-400 dark:text-gray-500">
            Imported from Gmail. Response can only be changed
            <a v-if="booking.html_link" :href="booking.html_link" target="_blank" rel="noopener noreferrer"
              class="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 underline">
              in Google Calendar
            </a>
            <span v-else>in Google Calendar</span>.
          </p>
        </div>
      </div>
    </Transition>

    <BookingNotesModal :open="notesOpen" :booking="booking" @close="notesOpen = false" />

    <!-- Guest profile hovercard (Timelydo users only) -->
    <Transition name="popover">
      <div
        v-if="hoverGuest"
        id="event-popover-hovercard"
        class="fixed z-[60] w-72 bg-white dark:bg-[#1C2128] rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 p-4"
        :style="hoverCardStyle"
        @mouseenter="cancelHoverHide"
        @mouseleave="onGuestLeave"
      >
        <div class="flex items-start gap-3">
          <img
            v-if="hoverGuest.picture"
            :src="hoverGuest.picture"
            :alt="hoverGuest.email"
            class="w-14 h-14 rounded-full object-cover flex-shrink-0"
          />
          <div v-else class="w-14 h-14 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center flex-shrink-0">
            <span class="text-xl font-semibold text-blue-600 dark:text-blue-300 uppercase">{{ (hoverGuest.display_name || hoverGuest.email || '?')[0] }}</span>
          </div>
          <div class="min-w-0 pt-1">
            <p class="text-base font-semibold text-gray-900 dark:text-white truncate">{{ hoverGuest.display_name || hoverGuest.email }}</p>
            <p class="text-sm text-gray-500 dark:text-gray-400 truncate">{{ hoverGuest.email }}</p>
          </div>
        </div>
        <div class="flex items-center gap-2 mt-4">
          <a
            v-if="hoverGuest.profile_url"
            id="event-popover-hovercard-profile-link"
            :href="'/' + hoverGuest.profile_url"
            target="_blank"
            rel="noopener noreferrer"
            class="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-sm font-medium rounded-full transition-colors"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
            </svg>
            Visit Profile
          </a>
          <a
            id="event-popover-hovercard-mail-link"
            :href="'mailto:' + hoverGuest.email"
            title="Send mail"
            class="w-9 h-9 rounded-full border border-gray-200 dark:border-gray-600 flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex-shrink-0"
          >
            <svg class="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
            </svg>
          </a>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onUnmounted } from 'vue'
import { useCalendarStore }         from 'stores/calendar.js'
import { useCalendarSettingsStore } from 'stores/calendarSettings.js'
import { useGlobalStore }           from 'stores/global.js'
import { useRequest }               from 'composables/useRequest.js'
import BookingNotesModal            from './BookingNotesModal.vue'

const store         = useCalendarStore()
const settingsStore = useCalendarSettingsStore()
const globalStore   = useGlobalStore()
const { axiosRequest } = useRequest()

const popoverEl         = ref(null)
const showDeleteConfirm = ref(false)
const deleting          = ref(false)
const error             = ref(null)
const meetLinkCopied    = ref(false)
const guestsCopied      = ref(false)
const guestsExpanded    = ref(false)
const locationCopied    = ref(false)
const notesOpen         = ref(false)
let _copiedTimer         = null
let _guestsCopiedTimer   = null
let _locationCopiedTimer = null

// ── Guest hovercard ───────────────────────────────────────────────────────────
const HOVER_CARD_WIDTH  = 288
const HOVER_CARD_HEIGHT = 160

const hoverGuest    = ref(null)
const hoverCardPos  = ref({ top: 0, left: 0 })
let _hoverHideTimer = null

const hoverCardStyle = computed(() => ({
  top:  `${hoverCardPos.value.top}px`,
  left: `${hoverCardPos.value.left}px`,
}))

function onGuestEnter(guest, e) {
  if (!guest.timelydo_user) return
  clearTimeout(_hoverHideTimer)
  const rect = e.currentTarget.getBoundingClientRect()
  const vw = window.innerWidth
  const vh = window.innerHeight
  const GAP = 10
  // Beside the row (right, flipping to left) so the other guest rows stay
  // hoverable while the card is open.
  let left = rect.right + GAP
  if (left + HOVER_CARD_WIDTH > vw - 8) left = rect.left - HOVER_CARD_WIDTH - GAP
  if (left < 8) left = 8
  let top = rect.top - 8
  if (top + HOVER_CARD_HEIGHT > vh - 8) top = vh - HOVER_CARD_HEIGHT - 8
  if (top < 8) top = 8
  hoverCardPos.value = { top, left }
  hoverGuest.value = guest
}

function onGuestLeave() {
  clearTimeout(_hoverHideTimer)
  _hoverHideTimer = setTimeout(() => { hoverGuest.value = null }, 250)
}

function cancelHoverHide() {
  clearTimeout(_hoverHideTimer)
}

const POPOVER_WIDTH      = 440
const POPOVER_MAX_HEIGHT = 750 // hard cap — panel never grows taller than this even with room to spare
const FALLBACK_HEIGHT    = 400 // used for the first frame, before the panel's real size is measured
const EDGE_MARGIN        = 16  // minimum space to keep from viewport edges

const booking = computed(() => store.eventPopoverBooking)

// ── Positioning ───────────────────────────────────────────────────────────────

const viewportSize   = ref({ w: window.innerWidth, h: window.innerHeight })
// Actual rendered height of the panel. Kept in sync via ResizeObserver so
// short bookings sit snugly beside the anchor, and the panel repositions
// itself live as content grows/shrinks (e.g. expanding the guest list).
const measuredHeight = ref(FALLBACK_HEIGHT)

let _resizeObserver = null

watch(popoverEl, (el) => {
  _resizeObserver?.disconnect()
  _resizeObserver = null
  if (!el) return
  _resizeObserver = new ResizeObserver(() => {
    measuredHeight.value = el.offsetHeight
  })
  _resizeObserver.observe(el)
})

function onWindowResize() {
  viewportSize.value = { w: window.innerWidth, h: window.innerHeight }
}
window.addEventListener('resize', onWindowResize)

onUnmounted(() => {
  _resizeObserver?.disconnect()
  window.removeEventListener('resize', onWindowResize)
})

const popoverStyle = computed(() => {
  const anchor = store.eventPopoverAnchor
  if (!anchor) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }

  const vw = viewportSize.value.w
  const vh = viewportSize.value.h
  const GAP = 8
  // Cap always applies (never taller than the viewport allows); actual
  // position uses the real measured height so short content stays snug.
  const cappedMax = Math.min(POPOVER_MAX_HEIGHT, vh - EDGE_MARGIN * 2)
  const height     = Math.min(measuredHeight.value, cappedMax)

  let left = anchor.right + GAP
  let top  = anchor.top

  if (left + POPOVER_WIDTH > vw) left = anchor.left - POPOVER_WIDTH - GAP
  if (top + height > vh - EDGE_MARGIN) top = vh - height - EDGE_MARGIN
  if (top < EDGE_MARGIN) top = EDGE_MARGIN
  if (left < 8) left = 8

  return { top: `${top}px`, left: `${left}px`, maxHeight: `${cappedMax}px` }
})

watch(() => store.eventPopoverOpen, (open) => {
  if (!open) {
    showDeleteConfirm.value = false
    deleting.value = false
    error.value = null
    meetLinkCopied.value = false
    guestsCopied.value = false
    guestsExpanded.value = false
    locationCopied.value = false
    hoverGuest.value = null
    rsvpSaving.value = null
    notesOpen.value = false
    clearTimeout(_copiedTimer)
    clearTimeout(_guestsCopiedTimer)
    clearTimeout(_locationCopiedTimer)
    clearTimeout(_hoverHideTimer)
  }
})

// ── Computed display values ───────────────────────────────────────────────────

const eventColor = computed(() =>
  booking.value?.color_code || booking.value?.backgroundColor || booking.value?.color || '#3b82f6'
)

const dateText = computed(() => {
  if (!booking.value) return ''
  const start = new Date(booking.value.start_at ?? booking.value.start)
  if (booking.value.all_day) {
    return start.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
  }
  const tz   = settingsStore.date_primary_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
  const is12h = globalStore.user?.time_format !== '24_hours'
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: is12h, timeZone: tz,
  }).format(start)
})

const timeText = computed(() => {
  if (!booking.value || booking.value.all_day) return ''
  const tz   = settingsStore.date_primary_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
  const is12h = globalStore.user?.time_format !== '24_hours'
  const fmt   = (d) => new Date(d).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: is12h, timeZone: tz })
  const start = booking.value.start_at ?? booking.value.start
  const end   = booking.value.end_at   ?? booking.value.end
  return `${fmt(start)} – ${fmt(end)}`
})

const durationText = computed(() => {
  if (!booking.value) return ''
  const start = new Date(booking.value.start_at ?? booking.value.start)
  const end   = new Date(booking.value.end_at   ?? booking.value.end)
  const mins  = Math.round((end - start) / 60000)
  if (mins < 60) return `${mins} min`
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return m ? `${h}h ${m}m` : `${h}h`
})

const visibilityText = computed(() => {
  const v = booking.value?.visibility
  if (!v || v === 'default') return null
  return v.charAt(0).toUpperCase() + v.slice(1)
})

const transparencyText = computed(() => {
  const t = booking.value?.transparency
  if (!t) return null
  return t === 'transparent' ? 'Free' : 'Busy'
})

const locationText = computed(() => {
  const loc = booking.value?.location
  if (!loc) return ''
  if (typeof loc === 'string') return loc
  return loc.address || loc.name || loc.url || ''
})

// "Main Place, Street, City, ..." → main heading + lighter remainder
const locationParts = computed(() => {
  const loc = locationText.value
  if (!loc) return null
  const idx = loc.indexOf(',')
  if (idx === -1) return { main: loc, secondary: '' }
  return { main: loc.slice(0, idx).trim(), secondary: loc.slice(idx + 1).trim() }
})

const currentUserPicture = computed(() => globalStore.user?.profile_picture_url || null)

function isCurrentUser(guest) {
  const email = globalStore.user?.email
  return !!email && guest.email === email
}

// Registered users get a picture from the API; the current user falls back to
// the global store in case the booking payload predates their registration.
function guestPicture(guest) {
  return guest.picture || (isCurrentUser(guest) ? currentUserPicture.value : null)
}

const RSVP_ORDER = { accepted: 0, declined: 1, tentative: 2 } // anything else (needsAction) last

function hasResponded(guest) {
  return guest.response_status in RSVP_ORDER
}

const guests = computed(() => {
  const attendees = booking.value?.attendees
  if (!attendees?.length) return []
  // Organizer always first, then by response: accepted, declined, tentative, no response
  const rank = (g) => (g.organizer === true ? -1 : RSVP_ORDER[g.response_status] ?? 3)
  return [...attendees].sort((a, b) => rank(a) - rank(b))
})

const GUEST_COLLAPSE_THRESHOLD = 5
const isGuestsCollapsible = computed(() => guests.value.length > GUEST_COLLAPSE_THRESHOLD)

const rsvpSummaryText = computed(() => {
  let yes = 0, no = 0, waiting = 0
  for (const g of guests.value) {
    if (g.response_status === 'accepted') yes++
    else if (g.response_status === 'declined') no++
    else if (g.response_status !== 'tentative') waiting++
  }
  const parts = []
  if (yes)     parts.push(`${yes} yes`)
  if (no)      parts.push(`${no} no`)
  if (waiting) parts.push(`${waiting} waiting`)
  return parts.join(', ')
})

const meetLinkDisplay = computed(() =>
  (booking.value?.google_meet_link || '').replace(/^https?:\/\//, '')
)

// Google event bodies are organizer-controlled HTML — sanitize before v-html.
const SAFE_URL = /^(https?:|mailto:|tel:|\/)/i

// Zero-width / directional-mark characters some editors leave behind (ZWSP,
// ZWNJ, ZWJ, LRM/RLM, BOM) — invisible, but not stripped by String#trim().
const INVISIBLE_CHARS_RE = new RegExp('[\\u200B-\\u200F\\uFEFF]', 'g')

const safeBody = computed(() => {
  const body = booking.value?.description || booking.value?.body
  if (!body) return ''
  const doc = new DOMParser().parseFromString(body, 'text/html')
  const visibleText = (doc.body.textContent || '').replace(INVISIBLE_CHARS_RE, '').trim()
  if (!visibleText) return ''

  doc.body.querySelectorAll('script, style, iframe, object, embed, form, link, meta').forEach(el => el.remove())
  doc.body.querySelectorAll('*').forEach(el => {
    for (const attr of [...el.attributes]) {
      const name = attr.name.toLowerCase()
      if (name.startsWith('on') || name === 'srcdoc') {
        el.removeAttribute(attr.name)
      } else if (['href', 'src', 'xlink:href'].includes(name) && !SAFE_URL.test(attr.value.trim())) {
        el.removeAttribute(attr.name)
      }
    }
    if (el.tagName === 'A') {
      el.setAttribute('target', '_blank')
      el.setAttribute('rel', 'noopener noreferrer')
    }
  })
  return doc.body.innerHTML.trim()
})

// ── Actions ───────────────────────────────────────────────────────────────────

async function copyMeetLink() {
  const link = booking.value?.google_meet_link
  if (!link) return
  try {
    await navigator.clipboard.writeText(link)
    meetLinkCopied.value = true
    clearTimeout(_copiedTimer)
    _copiedTimer = setTimeout(() => { meetLinkCopied.value = false }, 2000)
  } catch {
    // Clipboard unavailable (permissions / insecure context) — leave icon as-is
  }
}

// ── Going? RSVP ───────────────────────────────────────────────────────────────
const RSVP_OPTIONS = [
  { value: 'accepted',  label: 'Yes'   },
  { value: 'declined',  label: 'No'    },
  { value: 'tentative', label: 'Maybe' },
]

const rsvpSaving = ref(null)

const selfAttendee = computed(() => booking.value?.attendees?.find(a => a.self))

// Only Google bookings where the user is an attendee — Google rejects RSVP
// updates from anyone not on the guest list.
const isExternalAttendee = computed(() => booking.value?.context === 'ExternalBooking' && !!selfAttendee.value)

// can_rsvp is false for events Google auto-created from a Gmail email
// (flight/hotel confirmations) — Google rejects RSVP updates on those
// from any third-party client, so the buttons would always fail.
const showRsvp = computed(() => isExternalAttendee.value && booking.value?.can_rsvp !== false)
const rsvpLocked = computed(() => isExternalAttendee.value && booking.value?.can_rsvp === false)

const currentRsvp = computed(() =>
  booking.value?.calendar_owner_status || selfAttendee.value?.response_status || null
)

async function setRsvp(status) {
  if (rsvpSaving.value || status === currentRsvp.value) return
  const bookingId = booking.value.id
  const previous  = currentRsvp.value

  // Optimistic: restyle the UI immediately, revert only if Google rejects it
  store.updateBookingRsvp(bookingId, status)
  rsvpSaving.value = status
  error.value = null
  try {
    const res = await axiosRequest('PUT', '/api/v1/google_calendar_bookings/update_response_status', {
      calendar: { id: booking.value.calendar_id, booking: { id: bookingId, response_status: status } },
    }, false)
    if (res.js_error || !res.response?.success) {
      store.updateBookingRsvp(bookingId, previous)
      error.value = res.response?.message || 'Could not update your response.'
    }
  } catch {
    store.updateBookingRsvp(bookingId, previous)
    error.value = 'Something went wrong.'
  } finally {
    rsvpSaving.value = null
  }
}

async function copyGuestEmails() {
  const emails = guests.value.map(g => g.email).filter(Boolean).join(', ')
  if (!emails) return
  try {
    await navigator.clipboard.writeText(emails)
    guestsCopied.value = true
    clearTimeout(_guestsCopiedTimer)
    _guestsCopiedTimer = setTimeout(() => { guestsCopied.value = false }, 2000)
  } catch {
    // Clipboard unavailable (permissions / insecure context) — leave icon as-is
  }
}

async function copyLocation() {
  if (!locationText.value) return
  try {
    await navigator.clipboard.writeText(locationText.value)
    locationCopied.value = true
    clearTimeout(_locationCopiedTimer)
    _locationCopiedTimer = setTimeout(() => { locationCopied.value = false }, 2000)
  } catch {
    // Clipboard unavailable (permissions / insecure context) — leave icon as-is
  }
}

function openEdit() {
  const b = booking.value
  // Google-synced bookings are edited on Google's own event page — Weekler
  // has no write access to the underlying Google event otherwise.
  if (b?.html_link) {
    window.open(b.html_link, '_blank', 'noopener,noreferrer')
    return
  }
  store.closeEventPopover()
  store.openBookingSlideOver(b)
}

function confirmDelete() {
  showDeleteConfirm.value = true
}

async function deleteBooking() {
  if (!booking.value?.id) return
  deleting.value = true
  error.value    = null
  try {
    const res = await axiosRequest('DELETE', `/api/v1/bookings/${booking.value.id}`, {}, false)
    if (!res.js_error && (res.response?.success || res.status === 200 || res.status === 204)) {
      store.removeBooking(booking.value.id)
      store.closeEventPopover()
    } else {
      error.value = res.response?.message || 'Could not delete booking.'
    }
  } catch {
    error.value = 'Something went wrong.'
  } finally {
    deleting.value = false
  }
}
</script>

<style scoped>
.popover-enter-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.popover-leave-active { transition: opacity 0.1s ease, transform 0.1s ease; }
.popover-enter-from, .popover-leave-to { opacity: 0; transform: scale(0.96); }
</style>
